/**
 * 문서 미리보기 팝업용 HTML 문서 생성
 *
 * - contract: A4 세로 + 왼쪽 사이드바 (급여형태, 조항선택, 부가서류, 다운로드)
 * - payroll: B5 세로 (급여명세서) — 상단 툴바
 * - tripleheader: A4 가로 (급상여대장) — 상단 툴바
 */

const DOC_LAYOUTS = {
  contract: {
    pageWidth: '210mm',
    pageHeight: '297mm',
    padding: '15mm',
    bodyClass: 'doc-preview-layout-contract',
    pdfFormat: 'a4',
    pdfOrientation: 'portrait',
  },
  payroll: {
    pageWidth: '176mm',
    pageHeight: '250mm',
    padding: '5mm',
    bodyClass: 'doc-preview-layout-payroll',
    pdfFormat: 'b5',
    pdfOrientation: 'portrait',
  },
  tripleheader: {
    pageWidth: '297mm',
    pageHeight: '210mm',
    padding: '15mm',
    bodyClass: 'doc-preview-layout-tripleheader',
    pdfFormat: 'a4',
    pdfOrientation: 'landscape',
  },
};

export function buildPreviewDocument(contentHtml, options = {}) {
  const {
    mode = 'payroll',
    rowIndex = 0,
    total = 0,
    pdfFilename = 'document.pdf',
    contractTab,
    includeConsent,
    include33Tax,
    tripleToggles,
    wageType = 'monthly',
    customTitle = '',
    enabledSections = null,
    payslipStyle = 'doaan',
  } = options;

  const layout = DOC_LAYOUTS[mode] || DOC_LAYOUTS.payroll;
  const isPayroll = mode === 'payroll';
  const isContract = mode === 'contract';
  const isTriple = mode === 'tripleheader';

  const pdfFilenameAttr = (pdfFilename || 'document.pdf')
    .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // ── 계약서 모드: 사이드바 생성 ──
  let sidebarHtml = '';
  let wrapClass = 'doc-preview-screen-wrap';

  // 계약서 추가 옵션
  const {
    employees = [],           // [{globalIndex, tab, tabIndex, tabLabel, name}, ...]
    currentEmployeeIndex = 0,
    currentEmployeeName = '',
    wageValues = {},          // {yeonbong, hourly, daily, netPay}
    wageWarning = '',
  } = options;

  if (isContract) {
    wrapClass = 'doc-preview-screen-wrap doc-preview-sidebar-layout';
    const wageChecked = (t) => wageType === t ? 'checked' : '';
    const empName = (currentEmployeeName || '직원').replace(/"/g, '&quot;').replace(/</g, '&lt;');

    // 직원 리스트 JSON (팝업용 — globalIndex와 tab 정보 포함)
    const empListJson = JSON.stringify(employees).replace(/"/g, '&quot;').replace(/</g, '&lt;');

    // 감지값 표시
    const fmtVal = (v) => v != null ? String(v).replace(/</g, '&lt;') : '-';
    const wv = wageValues || {};
    const valuesHtml = `<div class="sidebar-values">
      <span>연봉: <strong>${fmtVal(wv.yeonbong)}</strong></span>
      <span>시급: <strong>${fmtVal(wv.hourly)}</strong></span>
      <span>일급: <strong>${fmtVal(wv.daily)}</strong></span>
      <span>네트: <strong>${fmtVal(wv.netPay)}</strong></span>
    </div>`;

    // 경고 메시지
    const warningHtml = wageWarning
      ? `<div class="sidebar-warning">${wageWarning.replace(/</g, '&lt;')}</div>`
      : '';

    sidebarHtml = `
<div class="doc-preview-sidebar">
  ${warningHtml}
  <div class="sidebar-group">
    <div class="sidebar-group-title">직원 선택</div>
    <button type="button" class="sidebar-employee-btn" id="employeeSelectorBtn" onclick="toggleEmployeePopup()">${empName} &#9660;</button>
    <div class="sidebar-employee-popup" id="employeePopup" style="display:none" data-employees="${empListJson}"></div>
  </div>
  <div class="sidebar-group">
    <div class="sidebar-group-title">급여 형태</div>
    <label class="sidebar-radio"><input type="radio" name="wageType" value="monthly" ${wageChecked('monthly')}
      onchange="window.opener&&window.opener.postMessage({type:'changeWageType',value:'monthly'},'*')"/>월급제</label>
    <label class="sidebar-radio"><input type="radio" name="wageType" value="daily" ${wageChecked('daily')}
      onchange="window.opener&&window.opener.postMessage({type:'changeWageType',value:'daily'},'*')"/>일급제</label>
    <label class="sidebar-radio"><input type="radio" name="wageType" value="hourly" ${wageChecked('hourly')}
      onchange="window.opener&&window.opener.postMessage({type:'changeWageType',value:'hourly'},'*')"/>시급제</label>
    ${valuesHtml}
  </div>
  <div class="sidebar-group">
    <div class="sidebar-group-title">계약서 제목</div>
    <input type="text" class="sidebar-title-input" id="contractTitleInput" value="${(customTitle || '').replace(/"/g, '&quot;')}"
      placeholder="제목을 입력하세요"
      oninput="var h1=document.querySelector('.contract-title');if(h1)h1.textContent=this.value;window.opener&&window.opener.postMessage({type:'changeTitle',value:this.value},'*')"/>
  </div>
  <div class="sidebar-group">
    <div class="sidebar-group-title">작성일</div>
    <input type="date" class="sidebar-title-input" id="signingDateInput" value="${options.signingDate || ''}"
      onchange="window.opener&&window.opener.postMessage({type:'changeSigningDate',value:this.value},'*')"/>
  </div>
  <div class="sidebar-group">
    <div class="sidebar-group-title">조항 선택</div>
    <div id="sectionToggles" data-enabled="${enabledSections ? JSON.stringify(enabledSections).replace(/"/g, '&quot;') : ''}"></div>
  </div>
  <div class="sidebar-group">
    <div class="sidebar-group-title">부가 서류</div>
    <label class="sidebar-toggle"><input type="checkbox" ${includeConsent ? 'checked' : ''}
      onchange="window.opener&&window.opener.postMessage({type:'toggleConsent',value:this.checked},'*')"/>개인정보제공동의서</label>
    <label class="sidebar-toggle"><input type="checkbox" ${include33Tax ? 'checked' : ''}
      onchange="window.opener&&window.opener.postMessage({type:'toggle33Tax',value:this.checked},'*')"/>3.3% 공제동의서</label>
  </div>
  <div class="sidebar-group sidebar-actions">
    <select class="sidebar-format-select" id="sidebarFormatSelect">
      <option value="pdf" selected>PDF</option>
      <option value="word">Word</option>
    </select>
    <button type="button" class="sidebar-btn sidebar-btn-download" onclick="handleDownload()">다운로드</button>
    <button type="button" class="sidebar-btn sidebar-btn-close" onclick="window.close()">닫기</button>
  </div>
</div>`;
  }

  // ── 비계약서 모드: 상단 툴바 ──
  let toolbarHtml = '';
  if (!isContract) {
    const navLabel = total > 0 ? `${(rowIndex + 1)} / ${total}` : '';
    const prevDisabled = !isPayroll || rowIndex <= 0;
    const nextDisabled = !isPayroll || total <= 0 || rowIndex >= total - 1;
    const prevClick = prevDisabled ? '' : 'onclick="window.opener&&window.opener.postMessage({type:\'payslipPrev\'},\'*\')"';
    const nextClick = nextDisabled ? '' : 'onclick="window.opener&&window.opener.postMessage({type:\'payslipNext\'},\'*\')"';

    let tripleToggleHtml = '';
    if (isTriple && tripleToggles) {
      tripleToggleHtml = `
      <span class="doc-preview-toolbar-separator"></span>
      <label class="doc-preview-toolbar-toggle"><input type="checkbox" ${tripleToggles.fulltime?'checked':''}
        onchange="window.opener&&window.opener.postMessage({type:'tripleToggle',tab:'fulltime',value:this.checked},'*')"/>상용직</label>
      <label class="doc-preview-toolbar-toggle"><input type="checkbox" ${tripleToggles.day?'checked':''}
        onchange="window.opener&&window.opener.postMessage({type:'tripleToggle',tab:'day',value:this.checked},'*')"/>일용직</label>
      <label class="doc-preview-toolbar-toggle"><input type="checkbox" ${tripleToggles.biz?'checked':''}
        onchange="window.opener&&window.opener.postMessage({type:'tripleToggle',tab:'biz',value:this.checked},'*')"/>사업소득</label>`;
    }

    toolbarHtml = `
<div class="doc-preview-toolbar" style="width:${layout.pageWidth}">
  ${isPayroll ? `
  <button type="button" class="doc-preview-toolbar-btn" ${prevClick} ${prevDisabled?'disabled':''} title="이전">\u2190 이전</button>
  <span class="doc-preview-toolbar-label">${navLabel}</span>
  <button type="button" class="doc-preview-toolbar-btn" ${nextClick} ${nextDisabled?'disabled':''} title="다음">다음 \u2192</button>
  <span class="doc-preview-toolbar-separator"></span>
  <select class="doc-preview-toolbar-select" onchange="window.opener&&window.opener.postMessage({type:'changePayslipStyle',value:this.value},'*')">
    <option value="doaan"${payslipStyle==='doaan'?' selected':''}>도안</option>
    <option value="signtalkbill"${payslipStyle==='signtalkbill'?' selected':''}>싸인톡빌</option>
  </select>
  <span class="doc-preview-toolbar-peek" title="마우스를 올리면 보정값(원본 대비 조정액) 표시. 클릭하면 고정됩니다."
    onmouseenter="corrEnter(this)" onmouseleave="corrLeave(this)" onclick="corrPin(this)">보정값 보기</span>
  ` : ''}
  ${tripleToggleHtml}
  ${isTriple ? `<span class="doc-preview-toolbar-peek" title="마우스를 올리면 보정값(원본 대비 조정액) 표시. 클릭하면 고정됩니다."
    onmouseenter="corrEnter(this)" onmouseleave="corrLeave(this)" onclick="corrPin(this)">보정값 보기</span>` : ''}
  <span class="doc-preview-toolbar-separator"></span>
  <select class="doc-preview-toolbar-select" id="downloadFormatSelect">
    <option value="pdf" selected>PDF</option>
    <option value="word">Word</option>
  </select>
  <button type="button" class="doc-preview-toolbar-btn doc-preview-toolbar-download" onclick="handleDownload()" title="현재 문서 다운로드">다운로드</button>
  ${isPayroll ? `<button type="button" class="doc-preview-toolbar-btn doc-preview-toolbar-download-all" onclick="handleDownloadAll()" title="전체 직원 일괄 다운로드">일괄 다운로드</button>
  <span class="doc-preview-toolbar-separator"></span>
  <input type="month" class="doc-preview-toolbar-select" id="rangeFrom" value="${options.rangeFrom || ''}" title="시작월" style="width:130px"/>
  <span style="color:#fff">~</span>
  <input type="month" class="doc-preview-toolbar-select" id="rangeTo" value="${options.rangeTo || ''}" title="종료월" style="width:130px"/>
  <button type="button" class="doc-preview-toolbar-btn doc-preview-toolbar-download-all" onclick="handleDownloadRange()" title="선택 기간 급여명세 다운로드">기간 다운로드</button>` : ''}
  <button type="button" class="doc-preview-toolbar-btn doc-preview-toolbar-close" onclick="window.close()">닫기</button>
</div>`;
  }

  // 계약서(월급제): 우상단 '보정값 보기' — 호버 시 재계산 보정값(빨간 (±#.##))을 드러냄. 재fetch 없음.
  // (급여명세서는 상단 툴바에 '보정값 보기' 요소로 배치)
  const correctionPeekHtml = (isContract && wageType === 'monthly') ? `
    <div class="correction-peek" title="마우스를 올리면 보정값 표시. 클릭하면 고정됩니다."
      onmouseenter="corrEnter(this)" onmouseleave="corrLeave(this)" onclick="corrPin(this)">보정값 보기</div>` : '';

  const printAreaHtml = `
<div class="${wrapClass}">
  ${sidebarHtml}
  <div class="doc-preview-content-area">
    ${correctionPeekHtml}
    ${toolbarHtml}
    <div class="doc-preview-print-area doc-preview-layout-${mode}" style="--layout-page-width:${layout.pageWidth};--layout-page-height:${layout.pageHeight};--layout-padding:${layout.padding};">
      ${(contentHtml || '').trim()}
    </div>
  </div>
</div>`;

  return `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>미리보기</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #6b6b6b; font-family: 'Malgun Gothic','Apple SD Gothic Neo',sans-serif; display: flex; flex-direction: column; min-height: 100vh; }

    /* ── 상단 툴바 (급여명세/급상여) ── */
    .doc-preview-toolbar {
      position: sticky; top: 0; z-index: 100;
      display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
      padding: 8px 12px; background: #e7f3ff; border-bottom: 1px solid #b8daff;
      box-sizing: border-box; border-radius: 0 0 6px 6px;
    }
    .doc-preview-toolbar-btn { padding: 6px 12px; font-size: 14px; border: 1px solid #0d6efd; border-radius: 6px; background: #0d6efd; color: #fff; cursor: pointer; }
    .doc-preview-toolbar-btn:hover:not(:disabled) { background: #0b5ed7; }
    .doc-preview-toolbar-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .doc-preview-toolbar-label { min-width: 4em; text-align: center; font-weight: 500; color: #333; }
    .doc-preview-toolbar-download { background: #198754; border-color: #198754; }
    .doc-preview-toolbar-download:hover { background: #157347; }
    .doc-preview-toolbar-pdf { background: #dc3545; border-color: #dc3545; }
    .doc-preview-toolbar-pdf:hover { background: #bb2d3b; }
    .doc-preview-toolbar-download-all { background: #0d6efd; border-color: #0d6efd; }
    .doc-preview-toolbar-download-all:hover { background: #0b5ed7; }
    .doc-preview-toolbar-close { background: #6c757d; border-color: #6c757d; }
    .doc-preview-toolbar-close:hover { background: #5c636a; }
    .doc-preview-toolbar-toggle { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; color: #333; cursor: pointer; padding: 5px 10px; border-radius: 6px; background: #fff; border: 1px solid #b8daff; white-space: nowrap; user-select: none; }
    .doc-preview-toolbar-toggle:hover { background: #d0e8ff; }
    .doc-preview-toolbar-toggle input[type="checkbox"] { margin: 0; cursor: pointer; }
    .doc-preview-toolbar-separator { width: 1px; height: 24px; background: #b8daff; margin: 0 2px; }
    .doc-preview-toolbar-peek { padding: 5px 12px; font-size: 13px; font-weight: 600; color: #d00; background: #fff; border: 1px solid #f1aeae; border-radius: 14px; cursor: help; user-select: none; white-space: nowrap; }
    .doc-preview-toolbar-peek:hover { background: #fff0f0; }
    .doc-preview-toolbar-peek.pinned { background: #d00; color: #fff; border-color: #d00; }
    .doc-preview-toolbar-peek.pinned::after, .correction-peek.pinned::after { content: ' 📌'; }
    .doc-preview-toolbar-select { padding: 5px 8px; font-size: 13px; border: 1px solid #b8daff; border-radius: 6px; background: #fff; cursor: pointer; }

    /* ── 레이아웃 ── */
    .doc-preview-screen-wrap { width: 100%; display: flex; flex-direction: column; align-items: center; }
    .doc-preview-content-area { flex: 1; display: flex; flex-direction: column; align-items: center; width: 100%; position: relative; }

    /* 계약서 보정값 보기 (우상단 호버 peek) */
    .correction-peek {
      position: absolute; top: 10px; right: 14px; z-index: 200;
      padding: 5px 12px; font-size: 12px; font-weight: 600;
      background: #fff; color: #d00; border: 1px solid #f1aeae; border-radius: 14px;
      cursor: help; user-select: none; box-shadow: 0 1px 4px rgba(0,0,0,0.12); white-space: nowrap;
    }
    .correction-peek:hover { background: #fff0f0; }
    .correction-peek.pinned { background: #d00; color: #fff; border-color: #d00; }
    .wage-correction { display: none; color: #d00; font-size: 0.82em; margin-left: 4px; font-weight: 600; white-space: nowrap; }
    .doc-preview-content-area.show-corrections .wage-correction { display: inline; }
    .wage-reconcile { display: none; }
    .doc-preview-content-area.show-corrections .wage-reconcile { display: table-row; }

    /* ── 사이드바 레이아웃 (계약서) ── */
    .doc-preview-sidebar-layout { flex-direction: row !important; align-items: stretch !important; }
    .doc-preview-sidebar {
      width: 240px; min-width: 240px; max-width: 240px;
      position: sticky; top: 0; height: 100vh; overflow-y: auto;
      background: #f8f9fa; border-right: 1px solid #dee2e6;
      padding: 12px; display: flex; flex-direction: column; gap: 8px;
      font-size: 13px; color: #333;
    }
    .sidebar-group { padding: 8px 0; border-bottom: 1px solid #e0e0e0; }
    .sidebar-group:last-child { border-bottom: none; }
    .sidebar-group-title { font-weight: 700; font-size: 12px; color: #555; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
    .sidebar-radio, .sidebar-toggle { display: flex; align-items: center; gap: 6px; padding: 3px 0; cursor: pointer; font-size: 13px; }
    .sidebar-radio input, .sidebar-toggle input { margin: 0; cursor: pointer; }
    .sidebar-title-input { width: 100%; padding: 5px 8px; font-size: 13px; border: 1px solid #ccc; border-radius: 4px; }
    .sidebar-title-input:focus { border-color: #0d6efd; outline: none; }
    .sidebar-section-cb { display: flex; align-items: center; gap: 5px; padding: 2px 0; cursor: pointer; font-size: 12px; }
    .sidebar-section-cb input { margin: 0; cursor: pointer; }
    .sidebar-actions { display: flex; gap: 6px; flex-wrap: wrap; padding-top: 8px; }
    .sidebar-btn { flex: 1; padding: 7px 4px; font-size: 12px; border: none; border-radius: 6px; cursor: pointer; color: #fff; font-weight: 600; min-width: 50px; text-align: center; }
    .sidebar-btn-download { background: #198754; flex: 1; }
    .sidebar-btn-download:hover { background: #157347; }
    .sidebar-format-select { padding: 5px 6px; font-size: 12px; border: 1px solid #ccc; border-radius: 4px; min-width: 60px; }
    .sidebar-btn-close { background: #6c757d; }
    .sidebar-btn-close:hover { background: #5c636a; }
    .sidebar-employee-btn { width: 100%; padding: 6px 10px; font-size: 13px; border: 1px solid #ccc; border-radius: 4px; background: #fff; cursor: pointer; text-align: left; }
    .sidebar-employee-btn:hover { border-color: #0d6efd; }
    .sidebar-employee-popup { position: absolute; left: 12px; right: 12px; max-height: 250px; overflow-y: auto; background: #fff; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 200; }
    .sidebar-employee-item { padding: 6px 10px; cursor: pointer; font-size: 12px; border-bottom: 1px solid #f0f0f0; }
    .sidebar-employee-item:hover { background: #e7f3ff; }
    .sidebar-employee-item.active { background: #d0e8ff; font-weight: 600; }
    .sidebar-section-separator { font-size: 11px; color: #888; margin: 6px 0 2px; font-weight: 600; }
    .sidebar-values { display: flex; flex-wrap: wrap; gap: 2px 10px; font-size: 11px; color: #666; margin-top: 6px; padding: 4px 6px; background: #f0f4f8; border-radius: 4px; }
    .sidebar-values span { white-space: nowrap; }
    .sidebar-values strong { color: #0066cc; }
    .sidebar-warning { padding: 6px 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; font-size: 12px; color: #856404; margin-bottom: 4px; }
    .sidebar-emp-tag { display: inline-block; font-size: 10px; padding: 1px 5px; border-radius: 3px; font-weight: 600; vertical-align: middle; }
    .sidebar-emp-tag-fulltime { background: #d4edda; color: #155724; }
    .sidebar-emp-tag-day { background: #cce5ff; color: #004085; }

    /* ── 문서 영역 ── */
    .doc-preview-print-area {
      width: var(--layout-page-width); padding: var(--layout-padding);
      background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,0.25);
      min-height: var(--layout-page-height);
    }
    .doc-preview-print-area:has(> .page) { padding: 0; background: transparent; box-shadow: none; }
    .doc-preview-print-area .page {
      height: var(--layout-page-height); min-height: var(--layout-page-height);
      box-sizing: border-box; page-break-after: always; margin-bottom: 16px;
      padding: var(--layout-padding); background: #fff; overflow: hidden;
      box-shadow: 0 0 0 1px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.12);
    }
    .doc-preview-print-area .page:last-child { page-break-after: auto; margin-bottom: 0; }
    .doc-preview-print-area table { width: 100%; border-collapse: collapse; margin: 8px 0; border: 1px solid #333; }
    .doc-preview-print-area th, .doc-preview-print-area td { border: 1px solid #333; padding: 3px 6px; }
    .doc-preview-print-area th { background: #eee; }
    .doc-preview-print-area .num { text-align: right; }
    .doc-preview-print-area .contract-value { color: #0066cc; font-weight: 500; }

    @media print {
      .doc-preview-toolbar, .doc-preview-sidebar { display: none !important; }
      body { background: #fff; }
      .doc-preview-screen-wrap, .doc-preview-sidebar-layout { display: block !important; }
      .doc-preview-print-area { box-shadow: none; margin: 0; min-height: auto; }
      .doc-preview-print-area .page { box-shadow: none; margin-bottom: 0; overflow: visible; }
    }
    @page { size: ${mode === 'contract' ? 'A4' : mode === 'payroll' ? '176mm 250mm' : 'A4 landscape'}; margin: 0; }

    /* tripleheader 스타일 */
    .doc-preview-print-area .triple-webview, .doc-preview-print-area .triple-webview * { font-size: 9px; font-family: "Malgun Gothic", sans-serif; }
    .doc-preview-print-area .triple-webview .triple-caption { margin-bottom: 6px; font-size: 9px; }
    .doc-preview-print-area .triple-webview table { border-collapse: collapse; min-width: 100%; table-layout: fixed; }
    .doc-preview-print-area .triple-webview th, .doc-preview-print-area .triple-webview td { border: 1px solid #333; padding: 2px 4px; font-size: 9px; }
    .doc-preview-print-area .triple-webview .th-top { background: #d9d9d9; text-align: center; }
    .doc-preview-print-area .triple-webview .td-num { background: #d9d9d9; }
    .doc-preview-print-area .triple-webview .td-name { background: #ffffcc; }
    .doc-preview-print-area .triple-webview .td-pay-sum { background: #fff2cc; }
    .doc-preview-print-area .triple-webview .td-deduct-sum { background: #ffd966; }
    .doc-preview-print-area .triple-webview .td-real-pay { background: #c6efce; }
    .doc-preview-print-area .triple-webview .num { text-align: right; }
  </style>
</head>
<body class="${layout.bodyClass}" data-doc-mode="${mode}">
${printAreaHtml}
  <script>
  /* 보정값 보기: hover 시 노출, 클릭 시 고정(pin) */
  function corrArea(el) { return el.closest('.doc-preview-content-area') || document.querySelector('.doc-preview-content-area'); }
  function corrEnter(el) { var a = corrArea(el); if (a) a.classList.add('show-corrections'); }
  function corrLeave(el) { if (el.classList.contains('pinned')) return; var a = corrArea(el); if (a) a.classList.remove('show-corrections'); }
  function corrPin(el) { el.classList.toggle('pinned'); var a = corrArea(el); if (a) { if (el.classList.contains('pinned')) a.classList.add('show-corrections'); else a.classList.remove('show-corrections'); } }

  function handleDownload() {
    var fmt = document.getElementById('downloadFormatSelect') || document.getElementById('sidebarFormatSelect');
    var val = fmt ? fmt.value : 'pdf';
    window.opener && window.opener.postMessage({type: val === 'pdf' ? 'downloadPdf' : 'downloadWord'}, '*');
  }

  function handleDownloadAll() {
    var fmt = document.getElementById('downloadFormatSelect');
    var val = fmt ? fmt.value : 'pdf';
    window.opener && window.opener.postMessage({type: 'downloadAllPayslips', format: val}, '*');
  }

  function handleDownloadRange() {
    var fmt = document.getElementById('downloadFormatSelect');
    var val = fmt ? fmt.value : 'pdf';
    var from = document.getElementById('rangeFrom');
    var to = document.getElementById('rangeTo');
    if (!from || !to || !from.value || !to.value) { alert('시작월과 종료월을 선택해주세요.'); return; }
    window.opener && window.opener.postMessage({type: 'downloadPayslipRange', format: val, from: from.value, to: to.value}, '*');
  }

  /* ── 직원 선택 팝업 ── */
  function toggleEmployeePopup() {
    var popup = document.getElementById('employeePopup');
    if (!popup) return;
    if (popup.style.display === 'none') {
      var raw = popup.getAttribute('data-employees');
      var emps; try { emps = JSON.parse(raw); } catch(e) { return; }
      popup.innerHTML = '';
      emps.forEach(function(emp) {
        var div = document.createElement('div');
        div.className = 'sidebar-employee-item';
        // 탭 태그 표시
        var tag = document.createElement('span');
        tag.className = 'sidebar-emp-tag sidebar-emp-tag-' + (emp.tab || 'fulltime');
        tag.textContent = emp.tabLabel || '상용';
        div.appendChild(tag);
        div.appendChild(document.createTextNode(' ' + emp.name));
        div.onclick = function() {
          popup.style.display = 'none';
          document.getElementById('employeeSelectorBtn').innerHTML = tag.outerHTML + ' ' + emp.name + ' \u25BC';
          window.opener && window.opener.postMessage({type: 'changeEmployee', index: emp.globalIndex}, '*');
        };
        popup.appendChild(div);
      });
      popup.style.display = 'block';
    } else {
      popup.style.display = 'none';
    }
  }
  document.addEventListener('click', function(e) {
    var popup = document.getElementById('employeePopup');
    var btn = document.getElementById('employeeSelectorBtn');
    if (popup && btn && !popup.contains(e.target) && e.target !== btn) popup.style.display = 'none';
  });

  /* ── 계약서 사이드바: 조항 체크 시 서버 re-fetch ── */
  (function(){
    var cw = document.querySelector('.contract-webview');
    if (!cw) return;
    var sectionsAttr = cw.getAttribute('data-sections');
    if (!sectionsAttr) return;
    var sections;
    try { sections = JSON.parse(sectionsAttr); } catch(e) { return; }
    var container = document.getElementById('sectionToggles');
    if (!container) return;

    // 제목 입력란 초기화
    var titleAttr = cw.getAttribute('data-contract-title') || '';
    var titleInput = document.getElementById('contractTitleInput');
    if (titleInput && !titleInput.value) titleInput.value = titleAttr;

    // 부모에서 전달된 선택 상태 복원
    var enabledRaw = container.getAttribute('data-enabled');
    var enabledSet = null;
    if (enabledRaw) {
      try { enabledSet = new Set(JSON.parse(enabledRaw)); } catch(e) {}
    }

    // 조항 체크박스 생성 (기본/추가 그룹 분리)
    var lastType = '';
    sections.forEach(function(sec) {
      if (sec.type !== lastType) {
        var sep = document.createElement('div');
        sep.className = 'sidebar-section-separator';
        sep.textContent = sec.type === 'optional' ? '추가 조항' : '기본 조항';
        container.appendChild(sep);
        lastType = sec.type;
      }
      var label = document.createElement('label');
      label.className = 'sidebar-section-cb';
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      // enabledSet이 있으면 그걸 따르고, 없으면 기본값
      cb.checked = enabledSet ? enabledSet.has(sec.id) : (sec.default !== false);
      cb.setAttribute('data-section-id', sec.id);
      cb.addEventListener('change', function() {
        sendEnabledSections();
      });
      label.appendChild(cb);
      label.appendChild(document.createTextNode(' ' + sec.label));
      container.appendChild(label);
    });

    function sendEnabledSections() {
      var cbs = container.querySelectorAll('input[type="checkbox"]');
      var enabled = [];
      cbs.forEach(function(cb) {
        if (cb.checked) enabled.push(cb.getAttribute('data-section-id'));
      });
      window.opener && window.opener.postMessage({type: 'changeSections', value: enabled}, '*');
    }
  })();

  /* ── 자동 페이지 분할 (Clone-and-Clip) ── */
  (function(){
    function paginate(){
      var area = document.querySelector('.doc-preview-print-area');
      if(!area) return;
      var ruler = document.createElement('div');
      ruler.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:var(--layout-page-height);padding-top:var(--layout-padding);box-sizing:border-box;';
      area.appendChild(ruler);
      var pageH = ruler.offsetHeight;
      var padPx = parseFloat(getComputedStyle(ruler).paddingTop) || 0;
      area.removeChild(ruler);
      var usable = pageH - padPx * 2;
      if(usable <= 0) return;
      var pages = Array.from(area.querySelectorAll(':scope > .page'));
      pages.forEach(function(pg, idx){
        var first = pg.firstElementChild;
        if(idx > 0 && first) first.setAttribute('data-page-break','1');
        while(pg.firstChild) area.insertBefore(pg.firstChild, pg);
        area.removeChild(pg);
      });
      var contentTop = area.getBoundingClientRect().top + padPx;
      var totalH = area.scrollHeight - padPx * 2;
      var breakYs = [];
      area.querySelectorAll('[data-page-break]').forEach(function(el){
        breakYs.push(el.getBoundingClientRect().top - contentTop);
        el.removeAttribute('data-page-break');
      });
      breakYs.sort(function(a,b){ return a - b; });
      if(totalH <= usable && breakYs.length === 0) return;
      function safeY(nomY){
        var tw = document.createTreeWalker(area, NodeFilter.SHOW_TEXT);
        var best = nomY; var nd;
        while(nd = tw.nextNode()){
          if(!nd.textContent.trim()) continue;
          var p = nd.parentElement; if(!p) continue;
          var pr = p.getBoundingClientRect();
          if(pr.top - contentTop >= nomY || pr.bottom - contentTop <= nomY - 4) continue;
          var rng = document.createRange(); rng.selectNodeContents(nd);
          var rects = rng.getClientRects();
          for(var i = 0; i < rects.length; i++){
            var lt = rects[i].top - contentTop, lb = rects[i].bottom - contentTop;
            if(lt < nomY && lb > nomY && lt > nomY - usable * 0.5){ if(lt < best) best = lt; }
          }
        }
        return best;
      }
      var slices = [], y = 0, bi = 0;
      while(y < totalH - 1){
        var end = y + usable;
        if(bi < breakYs.length && breakYs[bi] > y + 1 && breakYs[bi] < end){
          slices.push({ off: y, h: breakYs[bi] - y }); y = breakYs[bi]; bi++;
        } else {
          var safe = safeY(end);
          if(safe <= y + 1) safe = end;
          var h = Math.min(safe - y, totalH - y);
          slices.push({ off: y, h: h }); y = y + h;
          while(bi < breakYs.length && breakYs[bi] <= y) bi++;
        }
      }
      var srcHtml = area.innerHTML; area.innerHTML = '';
      slices.forEach(function(s){
        var pg = document.createElement('div'); pg.className = 'page';
        var clip = document.createElement('div');
        clip.style.cssText = 'overflow:hidden;height:' + s.h + 'px;';
        var body = document.createElement('div');
        body.innerHTML = srcHtml; body.style.marginTop = (-s.off) + 'px';
        clip.appendChild(body); pg.appendChild(clip); area.appendChild(pg);
      });
    }
    if(document.readyState === 'complete')
      requestAnimationFrame(function(){ requestAnimationFrame(paginate); });
    else
      window.addEventListener('load', function(){
        requestAnimationFrame(function(){ requestAnimationFrame(paginate); });
      });
  })();
  </script>
</body>
</html>`;
}
